import { cn } from '@resolute/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';


interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/** Compact numeric pagination with ellipses, keyboard/AT friendly. */
export function Pagination({ page, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = pageWindow(page, totalPages);

  const go = (p: number) => {
    onPageChange(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className="mt-10 flex items-center justify-center gap-1" aria-label="Pagination">
      <button
        type="button"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className="flex size-10 items-center justify-center rounded border border-border text-foreground transition-colors duration-fast ease-out-expo hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="px-2 text-muted-foreground" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => go(p)}
            aria-current={p === page ? 'page' : undefined}
            className={cn(
              'flex size-10 items-center justify-center rounded border text-sm transition-colors duration-base ease-out-expo',
              p === page
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-foreground hover:bg-muted',
            )}
          >
            {p}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => go(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
        className="flex size-10 items-center justify-center rounded border border-border text-foreground transition-colors duration-fast ease-out-expo hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
      >
        <ChevronRight className="size-4" aria-hidden="true" />
      </button>
    </nav>
  );
}

/** Produce a windowed page list like [1, '…', 4, 5, 6, '…', 20]. */
function pageWindow(current: number, total: number): (number | '…')[] {
  const delta = 1;
  const range: number[] = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i += 1) {
    range.push(i);
  }
  const withEnds: (number | '…')[] = [1];
  if (range[0] && range[0] > 2) withEnds.push('…');
  withEnds.push(...range);
  const last = range[range.length - 1];
  if (last != null && last < total - 1) withEnds.push('…');
  if (total > 1) withEnds.push(total);
  return withEnds;
}
